import { useState } from "react";
import { ChevronUp } from "lucide-react";
import { Numero } from "@/components/ui";
import { HojaInferior } from "@/components/ui/controles";
import { Cascada, Dumbbell, Medidor, BUCKET_ICONO } from "@/components/charts";
import { fmtMoney, fmtMoneyCorto, fmtPct } from "@/engine/format/esMX";
import type { Snapshot } from "@/engine/types";
import * as A from "@/features/foto/adaptadores";
import { useFotoEnVivo } from "../useFotoEnVivo";

/** Los 4 números y las mini-gráficas que se arman mientras el miembro llena. */
export function ResumenEnVivo({ s, compacto = false }: { s: Snapshot; compacto?: boolean }) {
  const cur = s.setup.currency;
  const k = A.kpis(s), f = A.fondo(s), a = A.allocation(s), c = A.cascada(s);
  const hayBalance = s.balance.totals.assets > 0 || s.balance.totals.liabilities > 0;
  const hayFlujo = s.flow.totals.incomeAnnual > 0 || s.flow.totals.expenseAnnual > 0;
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2">
        <Numero tamano="sm" etiqueta="Patrimonio neto" valor={hayBalance ? fmtMoney(s.photo.netWorth, cur) : "—"} nota={hayBalance ? undefined : "Falta: tus activos"} />
        <Numero tamano="sm" etiqueta="Tasa de ahorro" valor={hayFlujo ? fmtPct(k.ahorro.valor, 1) : "—"} nota={hayFlujo ? "ideal: arriba de 20%" : "Falta: ingresos y gastos"} />
        <Numero tamano="sm" etiqueta="% libertad" valor={hayFlujo ? fmtPct(k.libertad.valor, 1) : "—"} nota={hayFlujo ? "pasivos ÷ gastos" : undefined} />
        <Numero tamano="sm" etiqueta="Rinde lo invertible" valor={k.rendimiento.corregido === null ? "—" : fmtPct(k.rendimiento.corregido, 1)} nota={k.rendimiento.difiere ? `el modelo muestra ${fmtPct(k.rendimiento.excel, 1)}` : undefined} />
      </div>
      {f.estado !== "vacio" && (
        <div className="rounded-md border border-borde bg-white p-3">
          <div className="mb-1.5 flex justify-between text-sm"><span className="font-medium">Fondo de emergencia</span><span className="tabular">{fmtPct(f.datos.fundedPct, 0)}</span></div>
          <Medidor valor={f.datos.fundedPct} etiqueta="Fondo de emergencia" />
          {!compacto && <p className="mt-1.5 text-xs text-tinta-suave">{f.lectura}</p>}
        </div>
      )}
      {a.estado !== "vacio" && !compacto && (
        <div className="rounded-md border border-borde bg-white p-3">
          <div className="mb-2 text-sm font-medium">Tu allocation: hoy vs objetivo</div>
          <Dumbbell filas={a.datos.filas.map((x) => ({ etiqueta: `${BUCKET_ICONO[x.bucket]} ${x.bucket}`, a: x.hoy, b: x.objetivo, banda: x.banda }))} dominio={[0, 1]} etiquetaA="Hoy" etiquetaB="Objetivo" formato={(v) => fmtPct(v, 0)} />
        </div>
      )}
      {c.estado !== "vacio" && c.datos.filas.length > 1 && !compacto && (
        <div className="rounded-md border border-borde bg-white p-3">
          <div className="mb-2 text-sm font-medium">Tus objetivos financiados</div>
          <Cascada filas={c.datos.filas} formatoDinero={(v) => fmtMoneyCorto(v, cur)} compacta />
        </div>
      )}
    </div>
  );
}

/** Móvil: barra fija sobre la navegación con los números clave; abre una hoja con el panel completo. Desktop: columna fija. */
export function PanelEnVivo() {
  const { snapshot } = useFotoEnVivo();
  const [abierto, setAbierto] = useState(false);
  if (!snapshot) return null;
  const cur = snapshot.setup.currency;
  const hayBalance = snapshot.balance.totals.assets > 0;
  const hayFlujo = snapshot.flow.totals.incomeAnnual > 0;
  const resumen = [hayBalance && `Patrimonio ${fmtMoneyCorto(snapshot.photo.netWorth, cur)}`, hayFlujo && `Ahorro ${fmtPct(snapshot.photo.savingsRate, 0)}`, hayFlujo && `Libertad ${fmtPct(snapshot.photo.freedomPct, 0)}`].filter(Boolean).join(" · ");
  return (
    <>
      <aside className="hidden md:block">
        <div className="sticky top-20 flex flex-col gap-2">
          <h2 className="text-sm font-semibold text-tinta-suave">Tu foto se va armando</h2>
          <ResumenEnVivo s={snapshot} />
        </div>
      </aside>
      <button type="button" onClick={() => setAbierto(true)} className="fixed inset-x-0 z-30 flex items-center justify-between gap-2 border-t border-borde bg-white px-4 py-2.5 text-left text-sm shadow-[0_-2px_8px_rgba(0,0,0,.04)] md:hidden"
        style={{ bottom: "calc(57px + env(safe-area-inset-bottom))" }} aria-label="Ver tu foto en vivo">
        <span className="min-w-0 truncate"><span className="font-semibold">Tu foto:</span> <span className="tabular text-tinta-suave">{resumen || "llena tus datos y aquí aparecen tus números"}</span></span>
        <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
      </button>
      <HojaInferior abierta={abierto} onCerrar={() => setAbierto(false)} titulo="Tu foto se va armando">
        <ResumenEnVivo s={snapshot} />
      </HojaInferior>
    </>
  );
}
